// PortalApp — the customer portal's router, and the line between signed-in and
// signed-out.
//
// Replaces the design drop's CommerceApp.tsx, which held the current screen in
// `useState` to demonstrate the route → page → modal composition. The real thing
// routes on the URL: react-router owns which page is showing, and AppShell's
// `active` / `onNavigate` pair reads and writes that URL instead of a state
// variable.
//
// Routes render outside the shell where there is no navigation to offer the
// person in front of them: `login`, `signed-out`, and `claim`. Everything else
// is behind the session — signed out, it redirects to login, and the boot
// renewal in PortalSessionProvider is what decides which.
//
// And there are now TWO signed-in portals behind that line, not one. Which is
// drawn follows the shape of the identity GET /portal/api/me/ answered with:
// somebody acting for one customer gets the six customer screens, and a
// metricsIQ person acting across the fleet gets the three metricsIQ ones.
// Neither router contains the other's addresses, so an address that is not
// yours is not refused — it simply is not there, and lands in the same
// catch-all a typo lands in. That is deliberate: the service answers those
// addresses 404 rather than 403 precisely so that walking the address space
// tells a customer nothing, and a client that redirected differently for a
// real address than for a made-up one would give it back.
//
// Every page reads live data: Fleet and Metering (Phase 2), Home and Billing
// (Phase 3), and the subscription journeys, Users and the account modal
// (Phase 4) — the three subscription flows mirror the Django templates' own
// addresses (/<id>/, /<id>/provisioning/, /<id>/end/) so the two surfaces can
// be compared page by page while both exist. Each page renders its own
// loading, failed and nothing-recorded-yet states.
import { useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';

import { PortalSessionProvider } from './auth/PortalSessionProvider';
import { actsForOneCustomer, placesToActBesidesHere, scopeOf } from './auth/api';
import { usePortalSession } from './auth/context';
import { AccountModal, type AccountTab } from './components/AccountModal';
import { AllCustomersPage } from './pages/AllCustomersPage';
import { AppShell, type CmRoute } from './components/AppShell';
import { CancelSubscriptionPage } from './pages/CancelSubscriptionPage';
import { CustomerHoldingsPage } from './pages/CustomerHoldingsPage';
import { DeploymentRequestsPage } from './pages/DeploymentRequestsPage';
import { MeteringAlertsPage } from './pages/MeteringAlertsPage';
import { SeeAllAuditPage } from './pages/SeeAllAuditPage';
import { ClaimJourney } from './claim/ClaimJourney';
import { FleetCostsPage } from './pages/FleetCostsPage';
import { FleetRevenuePage } from './pages/FleetRevenuePage';
import { FleetPage } from './pages/FleetPage';
import { BillingPage } from './pages/BillingPage';
import { HomePage } from './pages/HomePage';
import { MeteringPage } from './pages/MeteringPage';
import { ProvisioningRequestPage } from './pages/ProvisioningRequestPage';
import { SignInPage } from './pages/SignInPage';
import { SignedOutPage } from './pages/SignedOutPage';
import { SubscriptionStatusPage } from './pages/SubscriptionStatusPage';
import { SubscriptionsPage } from './pages/SubscriptionsPage';
import { UsersPage } from './pages/UsersPage';
import { usePortalTheme } from './theme';

/** Where /portal lives. One constant so links and redirects cannot drift apart. */
const BASE = '/portal';

/** The addresses that render without a session. */
const LOGIN = `${BASE}/login`;
const SIGNED_OUT = `${BASE}/signed-out`;
/**
 * Where each nav entry lives, under /portal.
 *
 * A table rather than the identifier doubling as the path, which is what the
 * six customer routes did while every one of them was a single segment. The
 * metricsIQ screens are two segments deep — the prefix says whose they are,
 * matching the API addresses they read — so the identifier and the path have
 * come apart, and writing the mapping down once is what keeps a link and the
 * nav entry it lights up from drifting.
 */
const ROUTE_PATH: Record<CmRoute, string> = {
  home: '',
  subscriptions: 'subscriptions',
  fleet: 'fleet',
  metering: 'metering',
  billing: 'billing',
  users: 'users',
  'metricsiq-customers': 'metricsiq/customers',
  'metricsiq-deployments': 'metricsiq/deployments',
  'metricsiq-alerts': 'metricsiq/alerts',
  'metricsiq-fleet-costs': 'metricsiq/fleet-costs',
  'metricsiq-fleet-revenue': 'metricsiq/fleet-revenue',
  'metricsiq-audit': 'metricsiq/audit',
};

/** The address a nav entry leads to. */
function addressOf(route: CmRoute): string {
  const path = ROUTE_PATH[route];
  return path === '' ? BASE : `${BASE}/${path}`;
}

/** The page for one subscription, written down once. */
function subscriptionAddress(publicId: string): string {
  return `${BASE}/subscriptions/${publicId}`;
}

/**
 * Where a deployment under one subscription is asked for.
 *
 * Built from the subscription's own address rather than spelled out again:
 * the two are the same page's family, and the route table below mirrors them
 * — so a second copy of the string is a second place for the segment to
 * drift away from the route that has to match it.
 */
function provisioningAddress(publicId: string): string {
  return `${subscriptionAddress(publicId)}/provisioning`;
}

/** Where a subscription is ended. */
function cancellationAddress(publicId: string): string {
  return `${subscriptionAddress(publicId)}/end`;
}

/** Where a metricsIQ person's Home is. */
const STAFF_HOME = addressOf('metricsiq-customers');

/**
 * The nav entry to light up, read back off the URL.
 *
 * Longest path first, so `metricsiq/customers` wins over anything shorter that
 * shares its opening. `home` is the fallback rather than an entry to match,
 * because its path is empty and would otherwise match every address there is.
 */
const MATCHABLE_ROUTES: readonly [CmRoute, string][] = (
  Object.entries(ROUTE_PATH) as [CmRoute, string][]
)
  .filter(([, path]) => path !== '')
  .sort(([, a], [, b]) => b.split('/').length - a.split('/').length);

function activeRoute(pathname: string): CmRoute {
  const rest = pathname.slice(BASE.length).replace(/^\//, '');
  const found = MATCHABLE_ROUTES.find(
    ([, path]) => rest === path || rest.startsWith(`${path}/`),
  );
  return found ? found[0] : 'home';
}

/** The sign-in screen, or a bounce inwards for somebody already signed in. */
function SignInRoute() {
  const { session } = usePortalSession();

  if (session.status === 'checking') {
    return <SessionBeingChecked />;
  }
  if (session.status === 'signed-in') {
    return <Navigate to={BASE} replace />;
  }
  return <SignInPage />;
}

/** The signed-out confirmation. Reachable whatever the session says. */
function SignedOutRoute() {
  const navigate = useNavigate();
  return <SignedOutPage onSignInAgain={() => navigate(LOGIN)} />;
}

/**
 * The portal proper: the shell, the pages, and the session they require.
 *
 * The redirect is here rather than on each page so that adding a page cannot
 * add an unguarded one. The backend guards its own endpoints regardless — this
 * decides what is drawn, not what may be read.
 */
function SignedInPortal() {
  const { session, signOut, switchScope } = usePortalSession();
  const navigate = useNavigate();
  const location = useLocation();
  // `opened` counts openings so the modal can be keyed on it. AccountModal
  // seeds its active tab from the prop and does not sync it in an effect, so a
  // remount is what makes "open it on the security tab" land on that tab even
  // when the person last left it on another one.
  const [account, setAccount] = useState<{
    open: boolean;
    tab: AccountTab;
    opened: number;
  }>({ open: false, tab: 'profile', opened: 0 });

  if (session.status === 'checking') {
    return <SessionBeingChecked />;
  }
  if (session.status === 'signed-out') {
    return <Navigate to={LOGIN} replace />;
  }

  const go = (route: CmRoute) => {
    navigate(addressOf(route));
  };
  const openSubscription = (publicId: string) =>
    navigate(subscriptionAddress(publicId));
  // Home's banner for a subscription awaiting setup lands here. It used to
  // lead to the claim journey, which answers 404 without a hand-off in this
  // browser's session and left a signed-in customer circling; the page the
  // button names is this one, and it is reached by the subscription's own id.
  const openProvisioning = (publicId: string) =>
    navigate(provisioningAddress(publicId));
  const openCustomer = (publicId: string) =>
    navigate(`${STAFF_HOME}/${publicId}`);
  // Fleet Revenue's way to the source of a row's figure: act for that customer
  // and open their Billing page. The same switch the masthead offers, aimed at
  // one page rather than at `${BASE}` — the customer router draws next, and
  // Billing is in it.
  const openCustomerBilling = (publicId: string) => {
    void switchScope({ customer: publicId }).then(() => navigate(addressOf('billing')));
  };

  const identity = session.identity;

  return (
    <AppShell
      active={activeRoute(location.pathname)}
      onNavigate={go}
      identity={
        actsForOneCustomer(identity)
          ? {
              email: identity.user.email,
              customerName: identity.customer.name,
              elsewhere: placesToActBesidesHere(identity),
            }
          : {
              email: identity.user.email,
              role: identity.role,
              elsewhere: placesToActBesidesHere(identity),
            }
      }
      // A switch lands the session somewhere else entirely, so the route it
      // was on is not a route to stay on: the other table draws next and does
      // not contain the address. Sent to `${BASE}` and let whichever router
      // the new identity chose decide where that is — a customer's Home, or
      // the redirect to All Customers. Re-reading the identity is the
      // provider's job and it has already happened by the time this resolves.
      onSwitchTo={(place) => {
        void switchScope(scopeOf(place)).then(() => navigate(BASE));
      }}
      // The shell's menu offers the two tabs the modal has — see AccountModal
      // for why notifications and sessions are not among them. It used to
      // offer four and fold the other two onto Profile; the entry that needed
      // that is gone, so the tab arrives as one of the two and is used.
      onOpenAccount={(tab) =>
        setAccount((previous) => ({
          open: true,
          tab,
          opened: previous.opened + 1,
        }))
      }
      onSignOut={() => {
        void signOut().then(() => navigate(SIGNED_OUT));
      }}
    >
      {actsForOneCustomer(identity) ? (
        <CustomerRoutes
          onNavigate={go}
          onProvision={openProvisioning}
          onOpenSubscription={openSubscription}
        />
      ) : (
        <StaffRoutes
          onOpenCustomer={openCustomer}
          onOpenCustomerBilling={openCustomerBilling}
          onOpenAudit={() => go('metricsiq-audit')}
          onOpenAlerts={() => go('metricsiq-alerts')}
        />
      )}
      {/* The account modal reads GET /portal/api/account/, a customer endpoint,
          and draws the customer the membership names. There is neither for
          somebody acting across the fleet, so it is not mounted for them —
          the shell already withholds the three menu entries that open it, and
          this is the same rule said in the place that would have to be wrong
          twice for a refusal to reach a screen. */}
      {actsForOneCustomer(identity) && (
        <AccountModal
          key={`${account.tab}-${account.opened}`}
          open={account.open}
          tab={account.tab}
          onOpenChange={(open) => setAccount((previous) => ({ ...previous, open }))}
          onSignOut={() => {
            void signOut().then(() => navigate(SIGNED_OUT));
          }}
        />
      )}
    </AppShell>
  );
}

/**
 * What somebody acting for one customer may draw.
 *
 * Two route tables rather than one with guards on some of its entries, and the
 * split does the work a guard would have had to remember to do. **The
 * metricsIQ addresses are not in this table at all**, so a customer who types
 * `/portal/metricsiq/customers` falls through to the same catch-all that
 * `/portal/nonsense` falls through to and is sent to `${BASE}`. Nothing on the
 * client tells them apart, which is the point: the backend already answers
 * those addresses 404 rather than 403 so that a customer walking the address
 * space cannot learn that an operations surface exists, and a redirect that
 * behaved differently for a real staff address than for a made-up one would
 * hand back on the client what the service refused to say.
 */
function CustomerRoutes({
  onNavigate,
  onProvision,
  onOpenSubscription,
}: {
  onNavigate: (route: CmRoute) => void;
  onProvision: (publicId: string) => void;
  onOpenSubscription: (publicId: string) => void;
}) {
  return (
    <Routes>
      <Route
        index
        element={<HomePage onNavigate={onNavigate} onProvision={onProvision} />}
      />
      <Route path="fleet" element={<FleetPage />} />
      <Route path="metering" element={<MeteringPage />} />
      <Route path="billing" element={<BillingPage />} />
      <Route
        path="subscriptions"
        element={<SubscriptionsPage onOpen={onOpenSubscription} />}
      />
      <Route path="subscriptions/:publicId" element={<SubscriptionStatusRoute />} />
      <Route
        path="subscriptions/:publicId/provisioning"
        element={<ProvisioningRoute />}
      />
      <Route path="subscriptions/:publicId/end" element={<CancellationRoute />} />
      <Route path="users" element={<UsersPage />} />
      <Route path="*" element={<Navigate to={BASE} replace />} />
    </Routes>
  );
}

/**
 * What a metricsIQ person acting across the fleet may draw.
 *
 * The mirror image, and it is a mirror rather than a superset: none of the six
 * customer addresses is here either. Every one of them reads an endpoint that
 * answers this token 401, so a staff session landing on `/portal/fleet` from a
 * bookmark is sent to All Customers rather than shown a screen of refusals.
 *
 * **Home, for somebody who acts for no customer, is All Customers**, and it is
 * a redirect rather than a different page drawn at `${BASE}`: the address then
 * says which screen this is, the sidebar lights the entry that matches, and a
 * link copied out of the location bar leads back to what was on screen. The
 * customer's Home reads their packs, their billing and their metering, so it
 * is not a screen a staff token could render even if it were offered.
 */
function StaffRoutes({
  onOpenCustomer,
  onOpenCustomerBilling,
  onOpenAudit,
  onOpenAlerts,
}: {
  onOpenCustomer: (publicId: string) => void;
  onOpenCustomerBilling: (publicId: string) => void;
  onOpenAudit: () => void;
  onOpenAlerts: () => void;
}) {
  return (
    <Routes>
      <Route index element={<Navigate to={STAFF_HOME} replace />} />
      <Route
        path="metricsiq/customers"
        element={
          <AllCustomersPage
            onOpenCustomer={onOpenCustomer}
            onOpenAudit={onOpenAudit}
            onOpenAlerts={onOpenAlerts}
          />
        }
      />
      <Route
        path="metricsiq/customers/:publicId"
        element={<CustomerHoldingsRoute />}
      />
      <Route
        path="metricsiq/deployments"
        element={<DeploymentRequestsPage />}
      />
      <Route path="metricsiq/alerts" element={<MeteringAlertsPage />} />
      <Route path="metricsiq/fleet-costs" element={<FleetCostsPage />} />
      <Route
        path="metricsiq/fleet-revenue"
        element={<FleetRevenuePage onOpenCustomerBilling={onOpenCustomerBilling} />}
      />
      <Route path="metricsiq/audit" element={<SeeAllAuditPage />} />
      <Route path="*" element={<Navigate to={STAFF_HOME} replace />} />
    </Routes>
  );
}

/**
 * One customer's holdings, reading the identifier off the URL.
 *
 * A wrapper for the reason the subscription flows have one: the page takes the
 * identifier as a prop, so it is not a page that can only ever live at one
 * address. Back goes to All Customers, which is where this screen is reached
 * from and the only place it is linked.
 */
function CustomerHoldingsRoute() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();
  return (
    <CustomerHoldingsPage
      publicId={publicId}
      onBack={() => navigate(STAFF_HOME)}
    />
  );
}

/**
 * The three subscription flows, each reading its own identifier off the URL.
 *
 * Wrappers rather than inline elements because the pages take the identifier as
 * a prop: a page that read the route itself would be a page that could only
 * ever live at one address, and these three mirror the template URLs
 * deliberately — /subscriptions/<id>/, /provisioning/, /end/.
 */
function SubscriptionStatusRoute() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();
  return (
    <SubscriptionStatusPage
      publicId={publicId}
      onBack={() => navigate(`${BASE}/subscriptions`)}
      onRequestDeployment={() => navigate(provisioningAddress(publicId))}
      onEnd={() => navigate(cancellationAddress(publicId))}
    />
  );
}

function ProvisioningRoute() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();
  const status = () => navigate(subscriptionAddress(publicId));
  // The template redirects to the status page on success; so does this.
  return (
    <ProvisioningRequestPage publicId={publicId} onBack={status} onRecorded={status} />
  );
}

function CancellationRoute() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();
  return (
    <CancelSubscriptionPage
      publicId={publicId}
      onBack={() => navigate(subscriptionAddress(publicId))}
    />
  );
}

/**
 * The claim journey, outside the shell and outside the session requirement.
 *
 * Where it lands: the portal proper if they came to look around, or straight
 * into the deployment request if they took the finish screen's one open action.
 */
function ClaimRoute() {
  const navigate = useNavigate();
  return (
    <ClaimJourney
      onFinished={() => navigate(BASE)}
      onRequestDeployment={(publicId) => navigate(provisioningAddress(publicId))}
      onSignIn={() => navigate(LOGIN)}
    />
  );
}

/**
 * The boot renewal, in flight.
 *
 * Deliberately almost nothing. What it must not do is render either the sign-in
 * screen or the portal, because it does not yet know which is right and both
 * would flash on every reload.
 */
function SessionBeingChecked() {
  return <div className="min-h-screen bg-background" />;
}

export default function PortalApp() {
  usePortalTheme();

  return (
    <PortalSessionProvider>
      <Routes>
        <Route path="login" element={<SignInRoute />} />
        <Route path="signed-out" element={<SignedOutRoute />} />
        {/* The claim journey is out here with the other two deliberately.
            Somebody arriving from a marketplace hand-off has no session —
            often no account — so the claim cannot render inside the signed-in
            shell. Its credential is the hand-off in this browser's session,
            not a token; see subscriptions/claim_api.py. Nothing inside the
            portal links here: a signed-in customer has no hand-off to present,
            which is why Home's setup button now goes to the subscription's own
            provisioning page instead. */}
        <Route path="claim" element={<ClaimRoute />} />
        <Route path="*" element={<SignedInPortal />} />
      </Routes>
    </PortalSessionProvider>
  );
}
